(() => {
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const config = () => window.GooeyDuck || {};
  const experienceState = () => window.GooeyExperience?.state?.get?.() || null;
  const productCache = new Map();

  let refreshQueued = false;
  let cartMoment = null;
  let cartWasOpen = false;

  const enabled = () => (
    config().experience?.gooeyCommentary !== false &&
    !document.body?.classList.contains('experience-gooey-off')
  );

  const assetFor = pose => config().mascotAssets?.[pose] || config().mascotAssets?.neutral || '';

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const safeLower = value => String(value || '').trim().toLowerCase();

  const handleFromUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  };

  const roomFrom = snapshot => snapshot?.currentRoom || snapshot?.continuation?.room || null;
  const situationFrom = snapshot => snapshot?.currentSituation || snapshot?.continuation?.situation || null;
  const recommendationFrom = snapshot => snapshot?.continuation?.recommendation || snapshot?.selectedRecommendation || snapshot?.scentMatch?.primary || null;

  const hasRescueContext = snapshot => Boolean(
    snapshot?.currentRoom ||
    snapshot?.currentSituation ||
    snapshot?.currentProblem ||
    snapshot?.continuation?.room ||
    snapshot?.continuation?.situation ||
    snapshot?.scentMatch?.primary
  );

  const createFigure = (pose, modifier = '') => {
    const figure = create('figure', `gooey-layer3-figure${modifier ? ` ${modifier}` : ''}`);
    figure.dataset.gooeyPose = pose;
    figure.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.src = assetFor(pose);
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    figure.appendChild(image);
    return figure;
  };

  const createSpeech = (line, modifier = '') => {
    const bubble = create('div', `gooey-layer3-speech${modifier ? ` ${modifier}` : ''}`);
    const label = create('span', 'gooey-layer3-speech__label', 'GOOEY');
    const copy = create('p', 'gooey-layer3-speech__copy', line);
    const scribble = create('span', 'gooey-layer3-speech__scribble');
    scribble.setAttribute('aria-hidden', 'true');
    bubble.append(label, copy, scribble);
    return bubble;
  };

  const currentPageHandle = () => handleFromUrl(window.location.href);

  const getProduct = async handle => {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then(response => response.ok ? response.json() : null)
      .catch(() => null);
    productCache.set(handle, promise);
    return promise;
  };

  const hydrateProductMedia = async (host, recommendation) => {
    const handle = recommendation?.handle || handleFromUrl(recommendation?.url || recommendation?.destination || '');
    if (!host || !handle || host.dataset.productHydrated === handle) return;
    host.dataset.productHydrated = handle;
    const product = await getProduct(handle);
    if (!product || !host.isConnected) return;
    const src = product.featured_image || product.images?.[0];
    if (!src) return;
    let image = host.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.loading = 'lazy';
      image.decoding = 'async';
      host.appendChild(image);
    }
    image.src = src;
    image.alt = product.title || recommendation?.title || 'Recommended Gooey Duck product';
  };

  const dockState = snapshot => {
    const room = roomFrom(snapshot);
    const situation = situationFrom(snapshot);
    const visited = Array.isArray(snapshot?.visitedRooms) ? snapshot.visitedRooms.length : 0;
    const primary = snapshot?.scentMatch?.primary?.title;

    if (visited >= 4) return { pose: 'drinking', line: `${visited} rooms. This has become an investigation.` };
    if (primary) return { pose: 'impressed', line: `Okay. ${primary}. That actually tracks.` };
    if (situation?.label) return { pose: 'concerned', line: `Still dealing with ${safeLower(situation.label)}? I kept the evidence.` };
    if (room?.name) return { pose: 'neutral', line: `Still dealing with the ${safeLower(room.name)}? I kept your place.` };
    return { pose: 'neutral', line: 'I kept your place. Somebody had to.' };
  };

  const decorateDockTrigger = () => {
    qsa('.gooey-rescue-dock__trigger').forEach(trigger => {
      if (trigger.querySelector('[data-gooey-layer3-peek]')) return;
      const mascot = create('span', 'gooey-rescue-dock__trigger-mascot');
      mascot.dataset.gooeyLayer3Peek = 'true';
      const image = document.createElement('img');
      image.src = assetFor('peek');
      image.alt = '';
      image.loading = 'lazy';
      mascot.appendChild(image);
      trigger.prepend(mascot);
    });
  };

  const buildRoomCard = (snapshot, room, situation) => {
    const card = create('section', 'gooey-layer3-dock-card gooey-layer3-dock-card--room');
    const label = create('span', 'gooey-layer3-dock-card__eyebrow', 'CURRENT ROOM');
    const row = create('div', 'gooey-layer3-dock-card__row');
    const icon = create('span', 'gooey-layer3-dock-card__icon', '⌂');
    icon.setAttribute('aria-hidden', 'true');
    const copy = create('div', 'gooey-layer3-dock-card__copy');
    copy.append(
      create('strong', 'gooey-layer3-dock-card__title', room?.name || 'House evidence'),
      create('span', 'gooey-layer3-dock-card__subtitle', situation?.label || room?.area || 'Saved room'),
      create('p', 'gooey-layer3-dock-card__detail', situation?.problem || snapshot.currentProblem || room?.problem || 'Gooey kept the room context attached.')
    );
    row.append(icon, copy);
    card.append(label, row);
    return card;
  };

  const buildPickCard = (recommendation, snapshot) => {
    const url = recommendation?.url || recommendation?.destination || (recommendation?.handle ? `/products/${recommendation.handle}` : '');
    const card = create(url ? 'a' : 'section', 'gooey-layer3-dock-card gooey-layer3-dock-card--pick');
    if (url) card.href = url;
    const label = create('span', 'gooey-layer3-dock-card__eyebrow', "GOOEY'S PICK");
    const row = create('div', 'gooey-layer3-dock-card__row');
    const media = create('span', 'gooey-layer3-dock-card__media');
    const copy = create('div', 'gooey-layer3-dock-card__copy');
    const title = recommendation?.title || snapshot?.scentMatch?.primary?.title || 'Keep looking';
    copy.append(
      create('strong', 'gooey-layer3-dock-card__title', title),
      create('span', 'gooey-layer3-dock-card__subtitle', recommendation ? 'Recommended for this rescue' : 'No scent lead yet')
    );
    if (recommendation) copy.append(create('span', 'gooey-layer3-dock-card__badge', 'RECOMMENDED'));
    const arrow = create('span', 'gooey-layer3-dock-card__arrow', '→');
    arrow.setAttribute('aria-hidden', 'true');
    row.append(media, copy, arrow);
    card.append(label, row);
    if (recommendation) hydrateProductMedia(media, recommendation);
    return card;
  };

  const decorateDockPanel = snapshot => {
    const room = roomFrom(snapshot);
    const situation = situationFrom(snapshot);
    const recommendation = recommendationFrom(snapshot);
    const visited = Array.isArray(snapshot?.visitedRooms) ? snapshot.visitedRooms.length : 0;
    const state = dockState(snapshot);

    qsa('.gooey-rescue-dock__panel').forEach(panel => {
      if (panel.querySelector('[data-gooey-layer3-dock]')) return;

      panel.classList.add('gooey-layer3-dock');
      const head = panel.querySelector('.gooey-rescue-dock__head');
      const body = panel.querySelector('.gooey-rescue-dock__body');
      const actions = panel.querySelector('.gooey-rescue-dock__actions');
      if (!head || !body || !actions) return;

      const hero = create('div', 'gooey-layer3-dock-hero');
      hero.dataset.gooeyLayer3Dock = 'true';
      hero.append(createFigure(state.pose, 'gooey-layer3-dock-hero__figure'), createSpeech(state.line, 'gooey-layer3-dock-hero__speech'));
      head.insertAdjacentElement('afterend', hero);

      body.replaceChildren();
      body.append(buildRoomCard(snapshot, room, situation));
      body.append(buildPickCard(recommendation, snapshot));

      const stats = create('div', 'gooey-layer3-dock-stats');
      const roomsStat = create('div', 'gooey-layer3-dock-stat');
      roomsStat.append(create('span', '', 'ROOMS INSPECTED'), create('strong', '', String(visited || 1)), create('small', '', 'rooms'));
      const scentStat = create('div', 'gooey-layer3-dock-stat');
      scentStat.append(create('span', '', 'YOUR SCENT LEAD'), create('strong', '', snapshot?.scentMatch?.primary?.title || recommendation?.title || 'Still investigating'));
      stats.append(roomsStat, scentStat);
      body.append(stats);

      const originalLinks = [...actions.querySelectorAll('a')];
      const back = originalLinks.find(link => /back to house/i.test(link.textContent || ''));
      const quiz = originalLinks.find(link => /find another scent/i.test(link.textContent || ''));
      const clear = actions.querySelector('button');
      actions.replaceChildren();

      const recUrl = recommendation?.url || recommendation?.destination || (recommendation?.handle ? `/products/${recommendation.handle}` : '');
      const recHandle = recommendation?.handle || handleFromUrl(recUrl);
      if (recUrl && recHandle !== currentPageHandle()) {
        const continueLink = create('a', 'button button--accent gooey-layer3-dock__continue', 'Continue This Rescue →');
        continueLink.href = recUrl;
        actions.append(continueLink);
      }
      if (back) {
        back.textContent = 'Back to the House';
        back.className = 'button button--outline';
        actions.append(back);
      }
      if (quiz) {
        quiz.textContent = 'Find Your Scent Instead';
        quiz.className = 'button button--outline';
        actions.append(quiz);
      }
      if (clear) actions.append(clear);
    });
  };

  const rescueState = snapshot => {
    const room = roomFrom(snapshot);
    const source = snapshot?.continuation?.source || 'house';
    if (source === 'scent-quiz') return { pose: 'approved', line: 'Fine. You checked my work. This one still made the cut.' };
    return {
      pose: 'pointing',
      line: room?.name ? `Yeah. This is the one I was pointing at for the ${safeLower(room.name)}.` : 'Yeah. This is the one I was pointing at.'
    };
  };

  const decorateRescueCards = snapshot => {
    const state = rescueState(snapshot);
    qsa('.gooey-rescue-card').forEach(card => {
      if (card.querySelector('[data-gooey-layer3-product-rescue]')) return;
      const top = card.querySelector('.gooey-rescue-card__top');
      const title = card.querySelector('.gooey-rescue-card__title');
      const copy = card.querySelector('.gooey-rescue-card__copy');
      const actions = card.querySelector('.gooey-rescue-card__actions');
      if (!top || !title || !copy || !actions) return;

      const shell = create('div', 'gooey-layer3-product-rescue');
      shell.dataset.gooeyLayer3ProductRescue = 'true';
      const copyWrap = create('div', 'gooey-layer3-product-rescue__copy');
      const speech = createSpeech(state.line, 'gooey-layer3-product-rescue__speech');
      copyWrap.append(top, speech, title, copy, actions);
      const figure = createFigure(state.pose, 'gooey-layer3-product-rescue__figure');
      shell.append(copyWrap, figure);
      card.replaceChildren(shell);
    });
  };

  const quizState = snapshot => {
    const primary = snapshot?.scentMatch?.primary?.title;
    if (primary) return { pose: 'impressed', line: `Okay. ${primary}. That actually tracks.` };
    const room = roomFrom(snapshot);
    return {
      pose: 'sideEye',
      line: room?.name ? `Fine. Apparently we're doing the ${safeLower(room.name)} the hard way.` : "Fine. Apparently we're doing this the hard way."
    };
  };

  const decorateQuiz = snapshot => {
    const room = roomFrom(snapshot);
    const situation = situationFrom(snapshot);
    qsa('[data-gooey-quiz-context]').forEach(notice => {
      if (notice.querySelector('[data-gooey-layer3-quiz]')) return;
      const state = quizState(snapshot);
      const shell = create('div', 'gooey-layer3-quiz');
      shell.dataset.gooeyLayer3Quiz = 'true';
      const figure = createFigure(state.pose, 'gooey-layer3-quiz__figure');
      const right = create('div', 'gooey-layer3-quiz__right');
      right.append(createSpeech(state.line, 'gooey-layer3-quiz__speech'));
      const context = create('div', 'gooey-layer3-quiz__context');
      context.append(
        create('span', 'gooey-layer3-quiz__context-icon', '↝'),
        create('strong', '', snapshot?.scentMatch?.primary ? 'Result connected to your room.' : 'Running the quiz with your room context…'),
        create('small', '', [room?.name, situation?.label].filter(Boolean).join(' · ') || 'Your House context is still attached.')
      );
      right.append(context);
      shell.append(figure, right);
      notice.replaceChildren(shell);
    });
  };

  const cartState = snapshot => {
    if (!hasRescueContext(snapshot)) return null;
    if (cartMoment === 'celebrating') return { pose: 'celebrating', line: 'Good. Now the room has a chance.' };
    return { pose: 'approved', line: 'Approved. Try not to make this weird.' };
  };

  const decorateCart = snapshot => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const host = document.querySelector('#CartDrawerContent');
    if (!drawer || !host) return;

    const open = drawer.getAttribute('aria-hidden') === 'false';
    if (!open && cartWasOpen) cartMoment = null;
    cartWasOpen = open;

    const existing = host.querySelector('[data-gooey-layer3-cart]');
    const normalMoment = host.querySelector('[data-gooey-normal-cart-reaction]');
    if (!open || !host.querySelector('.drawer-line') || normalMoment) {
      existing?.remove();
      return;
    }

    const state = cartState(snapshot);
    if (!state) {
      existing?.remove();
      return;
    }

    if (existing) return;

    const moment = create('section', 'gooey-layer3-cart-moment');
    moment.dataset.gooeyLayer3Cart = 'true';
    moment.append(createFigure(state.pose, 'gooey-layer3-cart-moment__figure'), createSpeech(state.line, 'gooey-layer3-cart-moment__speech'));
    host.prepend(moment);
  };

  const decorateProductCards = snapshot => {
    const recommendation = recommendationFrom(snapshot);
    const leadHandle = recommendation?.handle || handleFromUrl(recommendation?.url || recommendation?.destination || '');
    const quizLead = snapshot?.scentMatch?.primary?.handle || '';
    const targetHandle = quizLead || leadHandle;

    qsa('.product-card').forEach(card => {
      const old = card.querySelector('[data-gooey-layer3-card-sighting]');
      const link = card.querySelector('a.product-card__link');
      const handle = handleFromUrl(link?.href || '');
      if (!targetHandle || handle !== targetHandle) {
        old?.remove();
        return;
      }
      if (old) return;
      const media = card.querySelector('.product-card__media');
      if (!media) return;
      const sighting = create('div', 'gooey-layer3-card-sighting');
      sighting.dataset.gooeyLayer3CardSighting = 'true';
      sighting.append(createFigure(quizLead ? 'impressed' : 'pointing', 'gooey-layer3-card-sighting__figure'));
      const tag = create('span', 'gooey-layer3-card-sighting__tag', quizLead ? 'YOUR SCENT LEAD' : "GOOEY'S PICK");
      sighting.append(tag);
      media.append(sighting);
    });
  };

  const decorate404 = () => {
    const empty = document.querySelector('[data-gooey-404-root]');
    if (!empty || empty.querySelector('[data-gooey-layer3-404]')) return;
    empty.classList.add('gooey-layer3-404');
    const group = create('div', 'gooey-layer3-404__moment');
    group.dataset.gooeyLayer3404 = 'true';
    group.append(createFigure('sideEye', 'gooey-layer3-404__figure'), createSpeech('You found the part of the site even I lost.', 'gooey-layer3-404__speech'));
    empty.append(group);
  };

  const removeDynamic = () => {
    qsa('[data-gooey-layer3-dock], [data-gooey-layer3-product-rescue], [data-gooey-layer3-quiz], [data-gooey-layer3-cart], [data-gooey-layer3-card-sighting], [data-gooey-layer3-peek], [data-gooey-layer3-404]').forEach(node => node.remove());
  };

  const refresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(() => {
      refreshQueued = false;
      if (!enabled()) {
        removeDynamic();
        return;
      }
      const snapshot = experienceState();
      if (snapshot) {
        decorateDockTrigger();
        if (hasRescueContext(snapshot)) decorateDockPanel(snapshot);
        decorateRescueCards(snapshot);
        decorateQuiz(snapshot);
        decorateCart(snapshot);
        decorateProductCards(snapshot);
      }
      decorate404();
    });
  };

  const observer = new MutationObserver(refresh);
  const start = () => {
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    refresh();
  };

  document.addEventListener('gooey:experience:statechange', refresh);
  document.addEventListener('gooey:experience:open', refresh);
  document.addEventListener('gooey:house:roomchange', refresh);
  document.addEventListener('gooey:house:situationchange', refresh);
  document.addEventListener('gooey:cart:updated', refresh);
  document.addEventListener('gooey:cart:opened', refresh);
  document.addEventListener('gooey:product:added', () => {
    cartMoment = hasRescueContext(experienceState()) ? 'celebrating' : null;
    refresh();
  });
  document.addEventListener('shopify:section:load', refresh);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();